/**
 * Serviço BFF (Backend for Frontend) para Recommendation Engine
 * Implementa o padrão de desacoplamento: Frontend → Data-Provider → Frontend → Recommendation-Engine
 */

interface ProviderData {
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  data?: any;
  error_message?: string;
}

interface AnalysisRequest {
  social_id: string;
  document_type: 'CPF' | 'CNPJ';
  business_area: 'PLD' | 'CREDIT' | 'COMPLIANCE';
  provider_data: Record<string, ProviderData>;
  additional_data?: Record<string, any>;
}

interface AnalysisResponse {
  analysis_id: string;
  social_id: string;
  recommendation: 'APROVADO' | 'REPROVADO' | 'PENDENTE';
  risk_score: number;
  analysis_date: string;
  rules_applied: string[];
  message: string;
}

class RecommendationBFFService {
  private readonly API_KEY: string;
  private readonly DATA_PROVIDER_BASE_URL: string;
  private readonly RECOMMENDATION_ENGINE_BASE_URL: string;

  constructor() {
    this.API_KEY = process.env.REACT_APP_ROUTER_API_KEY || process.env.REACT_APP_API_KEY || '';
    const routerUrl = (process.env.REACT_APP_ROUTER_API_URL || '').replace(/\/$/, ''); // Remove trailing slash
    this.DATA_PROVIDER_BASE_URL = `${routerUrl}/data-provider`;
    this.RECOMMENDATION_ENGINE_BASE_URL = `${routerUrl}/recommendation-engine`;
  }

  /**
   * Fluxo completo BFF: busca dados + análise de recomendação
   */
  async analyzeDocument(socialId: string, documentType: 'CPF' | 'CNPJ', businessArea: 'PLD' | 'CREDIT' | 'COMPLIANCE' = 'PLD'): Promise<AnalysisResponse> {
    try {
      console.log(`🔄 Iniciando análise BFF para ${socialId} (${documentType})`);
      
      // 1. Buscar dados de todos os providers
      const providerData = await this.fetchAllProviderData(socialId, documentType);
      
      // 2. Enviar dados consolidados para recommendation-engine
      const analysis = await this.sendToRecommendationEngine({
        social_id: socialId,
        document_type: documentType,
        business_area: businessArea,
        provider_data: providerData
      });
      
      console.log(`✅ Análise BFF concluída: ${analysis.recommendation}`);
      return analysis;
      
    } catch (error) {
      console.error('❌ Erro na análise BFF:', error);
      throw error;
    }
  }

  /**
   * Busca dados de todos os providers disponíveis
   */
  private async fetchAllProviderData(socialId: string, documentType: 'CPF' | 'CNPJ'): Promise<Record<string, ProviderData>> {
    const providers = ['INOVAMIND', 'ESCAVADOR', 'QUOD', 'QUOD-PJ'];
    const providerData: Record<string, ProviderData> = {};
    
    // Buscar dados de cada provider em paralelo
    const promises = providers.map(async (provider) => {
      try {
        console.log(`📡 Buscando dados do ${provider}...`);
        const data = await this.fetchProviderData(socialId, documentType, provider);
        
        providerData[provider] = {
          status: 'SUCCESS',
          data: data
        };
        
        console.log(`✅ ${provider}: dados obtidos com sucesso`);
        
      } catch (error) {
        console.warn(`⚠️ ${provider}: erro ao buscar dados`, error);
        
        providerData[provider] = {
          status: 'ERROR',
          error_message: error instanceof Error ? error.message : 'Erro desconhecido'
        };
      }
    });
    
    await Promise.allSettled(promises);
    
    const successCount = Object.values(providerData).filter(p => p.status === 'SUCCESS').length;
    console.log(`📊 Dados coletados: ${successCount}/${providers.length} providers com sucesso`);
    
    return providerData;
  }

  /**
   * Busca dados de um provider específico
   */
  private async fetchProviderData(socialId: string, documentType: 'CPF' | 'CNPJ', provider: string): Promise<any> {
    const endpoint = this.getProviderEndpoint(provider, documentType);
    const url = `${this.DATA_PROVIDER_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        document: socialId,
        document_type: documentType
      })
    });
    
    if (!response.ok) {
      throw new Error(`${provider} API error: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Mapeia provider para endpoint correto
   */
  private getProviderEndpoint(provider: string, documentType: 'CPF' | 'CNPJ'): string {
    const endpoints: Record<string, string> = {
      'INOVAMIND': '/inovamind/analyze',
      'ESCAVADOR': '/escavador/analyze',
      'QUOD': documentType === 'CPF' ? '/quod/analyze' : '/quod-pj/analyze',
      'QUOD-PJ': '/quod-pj/analyze'
    };
    
    return endpoints[provider] || `/providers/${provider.toLowerCase()}/analyze`;
  }

  /**
   * Retry com exponential backoff
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    initialDelay: number = 1000
  ): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;
        
        // Não retenta em erros 4xx (exceto 429)
        if (error.message?.includes('400') || 
            error.message?.includes('401') || 
            error.message?.includes('403') ||
            error.message?.includes('404')) {
          throw error;
        }
        
        // Última tentativa, lança erro
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Calcula delay com exponential backoff
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(`⏳ Retry ${attempt + 1}/${maxRetries} após ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError!;
  }

  /**
   * Envia dados consolidados para recommendation-engine
   * Método público para uso em telas que precisam análise direta
   */
  async sendToRecommendationEngine(request: AnalysisRequest): Promise<AnalysisResponse> {
    return this.retryWithBackoff(async () => {
      const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-with-data`;
      
      console.log(`🎯 Enviando para recommendation-engine:`, {
        social_id: request.social_id,
        document_type: request.document_type,
        business_area: request.business_area,
        providers_count: Object.keys(request.provider_data).length
      });
      
      // Timeout de 60 segundos para cold start do Lambda
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-api-key': this.API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Recommendation Engine error: ${response.status} ${response.statusText} - ${errorText}`);
        }
        
        return await response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Timeout ao aguardar resposta do recommendation-engine (60s). O Lambda pode estar iniciando (cold start).');
        }
        throw error;
      }
    });
  }

  /**
   * Análise de lote completo usando endpoint batch (1 única requisição)
   * Usa dados já processados do lote ao invés de refazer chamadas aos providers
   * 
   * IMPORTANTE: Mantém todos os reports originais para a tela de detalhes
   * A unificação por documento é feita apenas no frontend da tela de recomendações
   */
  async analyzeBatch(batchId: string): Promise<any> {
    try {
      console.log(`🔄 Iniciando análise batch do lote ${batchId}`);
      
      // 1. Buscar dados do lote no data-provider (já inclui reports processados)
      const batchData = await this.fetchBatchData(batchId);
      
      // 2. Preparar payload batch com todos os documentos
      if (batchData.reports && Array.isArray(batchData.reports)) {
        const documents = batchData.reports.map((report: any) => {
          // Construir provider_data a partir dos dados do report
          const providerData: Record<string, ProviderData> = {};
          
          // Extrair apenas os campos relevantes (excluir metadados)
          const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt', 
                                 'backend_recommendation', 'backend_reasons', 'backend_risk_score', 
                                 'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
                                 'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
          
          const reportData: any = {};
          Object.keys(report).forEach(key => {
            if (!excludeFields.includes(key)) {
              reportData[key] = report[key];
            }
          });
          
          const providerName = batchData.provider || 'UNKNOWN';
          providerData[providerName] = {
            status: 'SUCCESS',
            data: reportData
          };
          
          return {
            social_id: report.document || report.social_id,
            document_type: report.document_type || 'CNPJ',
            business_area: 'PLD',
            provider_data: providerData
          };
        });
        
        console.log(`📦 Preparando batch de ${documents.length} documentos`);
        
        // 3. Enviar documentos com chunking para evitar timeout
        const batchResults = await this.sendBatchToRecommendationEngineWithChunking(
          documents,
          100,
          (processed, total) => {
            console.log(`📊 Progresso: ${processed}/${total} documentos processados (${Math.round(processed/total*100)}%)`);
          }
        );
        
        // 4. Enriquecer reports com resultados
        batchData.reports.forEach((report: any, index: number) => {
          const result = batchResults.results[index];
          if (result) {
            report.backend_recommendation = result.recommendation;
            report.backend_reasons = result.rules_applied;
            report.backend_risk_score = result.risk_score;
            report.backend_analysis_id = result.analysis_id;
            
            console.log(`✅ Documento ${index + 1}/${documents.length}: ${report.document} -> ${result.recommendation}`);
          }
        });
        
        console.log(`✅ Análise batch concluída: ${batchResults.total_processed} processados, ${batchResults.total_errors} erros`);
      }
      
      return batchData;
      
    } catch (error) {
      console.error('❌ Erro na análise batch do lote:', error);
      throw error;
    }
  }

  /**
   * Envia lote completo para recommendation-engine (1 única requisição)
   * Método público para uso em telas que precisam análise batch direta
   */
  async sendBatchToRecommendationEngine(documents: any[]): Promise<any> {
    return this.retryWithBackoff(async () => {
      const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-batch`;
      
      console.log(`🎯 Enviando batch de ${documents.length} documentos para recommendation-engine`);
      
      // Timeout de 120 segundos para processar lote grande
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-api-key': this.API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ documents }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Batch analysis error: ${response.status} ${response.statusText} - ${errorText}`);
        }
        
        const result = await response.json();
        console.log(`✅ Batch processado: ${result.total_processed} documentos, ${result.total_errors} erros`);
        
        return result;
        
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Timeout ao processar batch (120s). Tente com menos documentos ou aguarde.');
        }
        throw error;
      }
    });
  }

  /**
   * Envia lote grande em chunks para evitar timeout do API Gateway (29s)
   * Processa N documentos por vez com progresso
   */
  async sendBatchToRecommendationEngineWithChunking(
    documents: any[], 
    chunkSize: number = 100,
    onProgress?: (processed: number, total: number) => void
  ): Promise<any> {
    const allResults: any[] = [];
    let totalProcessed = 0;
    let totalErrors = 0;
    const totalChunks = Math.ceil(documents.length / chunkSize);

    console.log(`🎯 Processando ${documents.length} documentos em ${totalChunks} chunks de ${chunkSize}`);

    for (let i = 0; i < documents.length; i += chunkSize) {
      const chunk = documents.slice(i, i + chunkSize);
      const chunkNumber = Math.floor(i / chunkSize) + 1;
      
      try {
        console.log(`📦 Processando chunk ${chunkNumber}/${totalChunks} (${chunk.length} documentos)`);
        
        const chunkResult = await this.sendBatchToRecommendationEngine(chunk);
        
        // Acumular resultados mantendo a ordem original
        allResults.push(...chunkResult.results);
        totalProcessed += chunkResult.total_processed;
        totalErrors += chunkResult.total_errors;

        // Callback de progresso
        if (onProgress) {
          onProgress(Math.min(i + chunkSize, documents.length), documents.length);
        }

        console.log(`✅ Chunk ${chunkNumber}/${totalChunks} concluído: ${chunkResult.total_processed} processados, ${chunkResult.total_errors} erros`);

      } catch (error) {
        console.error(`❌ Erro no chunk ${chunkNumber}/${totalChunks}:`, error);
        totalErrors += chunk.length;
        
        // Adicionar resultados com erro para manter índices corretos
        allResults.push(...chunk.map(doc => ({
          social_id: doc.social_id,
          document_type: doc.document_type,
          recommendation: 'ERRO',
          rules_applied: [],
          risk_score: 0,
          error: 'Chunk processing failed'
        })));
      }

      // Pequeno delay entre chunks para evitar sobrecarga
      if (i + chunkSize < documents.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    console.log(`✅ Processamento completo: ${totalProcessed}/${documents.length} processados, ${totalErrors} erros`);

    return {
      results: allResults,
      total_processed: totalProcessed,
      total_errors: totalErrors
    };
  }

  /**
   * Busca dados do lote no data-provider
   */
  private async fetchBatchData(batchId: string): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/${batchId}/page-view`;
    
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Batch data error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Se retornou erro interno, lançar exceção
    if (data.error) {
      throw new Error(`API error: ${data.error}`);
    }
    
    console.log('📦 Dados do lote retornados:', data);
    return data;
  }

  /**
   * Lista todos os lotes disponíveis (usa data-provider diretamente)
   */
  async fetchBatchList(): Promise<any> {
    // Para listagem de lotes, usar data-provider diretamente é correto
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list`;
    
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Lista lotes com filtros (compatível com PageList)
   */
  async fetchBatchListWithFilters(params: URLSearchParams): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list?${params.toString()}`;
    
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }
}

// Instância singleton
export const recommendationBFFService = new RecommendationBFFService();

// Export da classe para testes
export { RecommendationBFFService };
